import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import {
  Carousel as AriaCarousel,
  CarouselScroller as AriaCarouselScroller,
  CarouselItem as AriaCarouselItem,
  CarouselTabs as AriaCarouselTabs,
  CarouselTab as AriaCarouselTab,
} from 'react-aria-carousel';
import styles from './Carousel.module.css';
import { CarouselButton } from './CarouselButton/CarouselButton';
import { CarouselDots } from './CarouselDots/CarouselDots';
import { AutoplayControl } from './AutoplayControl/AutoplayControl';
import { focusStyleVariants } from '../../utils/focus/focusStyles';
import { useFocusRing } from '@react-aria/focus';

import {
  WithResponsive,
  resolveResponsiveProp,
} from '../../utils/breakpoint/responsive';
import { useBreakpoint } from '../../utils/breakpoint/hooks/useBreakpoint';

const carousel = cva(styles.carousel, {
  variants: {
    looping: {
      infinite: styles.infinite,
      backToStart: styles.native,
      off: undefined,
    },
  },
});

export interface CarouselBaseProps {
  /**
   * Content to be displayed in the carousel
   */
  children?: React.ReactNode | React.ReactNode[];

  /**
   * Accessible name for the carousel
   * Describes the purpose or content of the carousel for screen readers
   * @example "Featured products" or "Customer testimonials"
   */
  'aria-label'?: string;

  /**
   * ID of an element that provides additional description for the carousel
   * Can reference an element that gives more context about the carousel's content
   * @example "carousel-description"
   */
  'aria-describedby'?: string;

  /**
   * Looping behavior
   * - 'infinite': Carousel loops continuously
   * - 'backToStart': Carousel loops back to start after reaching end
   * - 'off': Looping is disabled
   * @default 'off'
   */
  looping?: 'infinite' | 'backToStart' | 'off';

  /**
   * Number of items to display per page (minimum: 1)
   * Decimal values (e.g., 2.25) can be used to show partial items
   * @default 1
   * @min 1
   */
  itemsPerPage?: number;

  /**
   * Space between carousel items in pixels
   * @default 0
   */
  spaceBetweenItems?: number;

  /**
   * Amount of padding added to the scroll container to create a "peek" effect.
   * This shows a portion of the adjacent items.
   * Accepts any valid CSS dimension (px, rem, em, %) - e.g., "15%", "20px", "2rem"
   * @example "15%" or "20px" or "2rem"
   * @default undefined
   */
  scrollPadding?: string;

  /* Button and Controls Props */
  /**
   * Icon type for navigation buttons
   * @default 'chevron'
   */
  iconType?: 'chevron';

  /**
   * Style of carousel arrow buttons
   * @default 'neutral'
   */
  arrowStyleVariant?:
    | 'neutral'
    | 'white'
    | 'shapeFlat'
    | 'shapeElevated'
    | 'gradient';

  /**
   * Size of navigation buttons
   * @default 'md'
   */
  arrowSize?: 'sm' | 'md' | 'lg';

  /**
   * Whether to hide navigation buttons when disabled
   * Note: Navigation buttons are automatically hidden when all items fit on one page
   * @default false
   */
  hideDisabledArrow?: boolean;

  /**
   * Whether to show arrows only when hovering over the carousel or when it has focus
   * @default false
   */
  showArrowsOnHover?: boolean;

  /**
   * Focus style for interactive elements
   * @default 'default'
   */
  focusStyle?: 'default' | 'white';

  /* Dots/Tabs Props */
  /**
   * Size of pagination dots
   * @default 'md'
   */
  dotsSize?: 'md' | 'lg';

  /**
   * Visual style of pagination dots
   * @default 'standard'
   */
  dotsVariant?: 'standard' | 'transparent';

  /**
   * Whether to hide pagination dots
   * Note: Dots are automatically hidden when there's only one page
   * @default false
   */
  hideDots?: boolean;

  /* Autoplay Props */
  /**
   * Whether the autoplay is enabled
   * @default false
   */
  autoPlay?: boolean;

  /**
   * Interval in milliseconds between slides during autoplay
   * @default 2000
   */
  autoPlayInterval?: number;

  /**
   * Style variant for autoplay control button
   * @default 'neutralVibrant'
   */
  autoPlayStyleVariant?:
    | 'neutralVibrant'
    | 'neutralSubtle'
    | 'whiteVibrant'
    | 'whiteSubtle';

  /**
   * Size of autoplay control button
   * @default 'md'
   */
  autoPlayControlSize?: 'sm' | 'md';

  /**
   * Whether to enable mouse dragging for the carousel
   * @default true
   */
  mouseDragging?: boolean;

  /* Custom Class Names for Positioning and Styling */
  /**
   * Class name for the outermost wrapper of the carousel
   */
  carouselWrapperClassName?: string;

  /**
   * Class name for the previous button
   * Use this to position the previous button
   */
  prevArrowClassName?: string;

  /**
   * Class name for the next button
   * Use this to position the next button
   */
  nextArrowClassName?: string;

  /**
   * Class name for the autoplay control
   * Use this to position the autoplay button
   */
  autoplayControlClassName?: string;

  /**
   * Class name for the dots container
   * Use this to position the pagination dots
   */
  dotsContainerClassName?: string;

  /**
   * Class name for the dots wrapper
   * Use this to style the dots wrapper
   */
  dotsWrapperClassName?: string;

  /**
   * Class name for individual dots
   */
  dotClassName?: string;

  /**
   * Class name for the active dot
   */
  activeDotClassName?: string;

  /**
   * Class name for individual carousel item wrappers
   * Use this to style the wrapper div around each carousel item
   */
  itemWrapperClassName?: string;

  /**
   * Class name for the scroll container
   * Use this to customize the grid/scroll layout of carousel items
   */
  scrollerClassName?: string;

  /**
   * Class name for the currently active/visible carousel item
   * This class is applied to the item that corresponds to the current page
   */
  activeItemClassName?: string;

  /**
   * Callback fired when a carousel navigation button (prev/next) is pressed
   */
  onArrowPress?: (direction: 'prev' | 'next') => void;

  /**
   * Callback fired when a dot is pressed
   */
  onDotPress?: (index: number) => void;

  /**
   * Callback fired when the autoplay button is pressed
   */
  onAutoplayPress?: (state: 'play' | 'pause') => void;

  /**
   * Imperative controls ref for driving carousel navigation from external UI.
   */
  controlsRef?: React.MutableRefObject<CarouselControls | null>;

  /**
   * Callback fired when carousel navigation state changes.
   */
  onNavigationStateChange?: (state: CarouselNavigationState) => void;

  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

export interface CarouselControls {
  prev: () => void;
  next: () => void;
  goToSlide: (index: number) => void;
  canGoPrev: boolean;
  canGoNext: boolean;
  currentPage: number;
  totalPages: number;
}

export interface CarouselNavigationState {
  canGoPrev: boolean;
  canGoNext: boolean;
  currentPage: number;
  totalPages: number;
}

const toNavigationState = (
  canGoPrev: boolean,
  canGoNext: boolean,
  currentPage: number,
  totalPages: number,
): CarouselNavigationState => ({
  canGoPrev,
  canGoNext,
  currentPage,
  totalPages,
});

const getScrollMetrics = (
  scroller: HTMLDivElement,
  currentPage: number,
  totalPages: number,
) => {
  const maxScrollLeft = Math.max(
    0,
    scroller.scrollWidth - scroller.clientWidth,
  );
  const scrollLeft = scroller.scrollLeft;
  // Mouse wheel / trackpad scrolling can move by sub-pixel amounts.
  const epsilon = 0.01;
  const endEpsilon = END_SCROLL_EPSILON;
  const hasScrollableOverflow = maxScrollLeft > epsilon;

  return {
    endEpsilon,
    epsilon,
    hasScrollableOverflow,
    maxScrollLeft,
    nextCanScrollPrev: hasScrollableOverflow
      ? scrollLeft > epsilon
      : currentPage > 0,
    nextCanScrollNext: hasScrollableOverflow
      ? scrollLeft < maxScrollLeft - endEpsilon
      : currentPage < totalPages - 1,
    scrollLeft,
  };
};

const getCachedItemElements = (
  scroller: HTMLDivElement,
  itemElementsRef: React.MutableRefObject<HTMLElement[]>,
) => {
  if (itemElementsRef.current.length > 0) {
    return itemElementsRef.current;
  }

  const nextItemElements = Array.from(
    scroller.querySelectorAll<HTMLElement>('[data-item-index]'),
  );
  itemElementsRef.current = nextItemElements;
  return nextItemElements;
};

const resolveFirstVisibleItemIndex = (
  scroller: HTMLDivElement,
  itemElements: HTMLElement[],
  scrollLeft: number,
  epsilon: number,
) => {
  // react-aria-carousel applies paddingInline=scrollPadding to the scroller,
  // so include computed padding-left to align visibility math correctly.
  const scrollerPaddingLeft =
    parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
  const viewportStart = scrollLeft + scrollerPaddingLeft + epsilon;
  const nextFirstVisibleItemIndex =
    itemElements.find((item) => {
      const itemEnd = item.offsetLeft + item.offsetWidth;
      return itemEnd > viewportStart;
    })?.dataset.itemIndex ?? '0';

  const parsedFirstVisibleItemIndex = Number.parseInt(
    nextFirstVisibleItemIndex,
    10,
  );
  return Number.isNaN(parsedFirstVisibleItemIndex)
    ? 0
    : parsedFirstVisibleItemIndex;
};

const syncItemInteractivity = (
  itemElements: HTMLElement[],
  firstVisibleItemIndex: number,
  safeItemsPerPage: number,
) => {
  // Keep the currently visible item window interactive even before
  // the underlying carousel page index snaps to the next page start.
  const visibleItemCount = Math.max(1, Math.ceil(safeItemsPerPage));
  const visibleWindowEndIndex = firstVisibleItemIndex + visibleItemCount - 1;

  itemElements.forEach((item) => {
    const itemIndexString = item.dataset.itemIndex;
    if (typeof itemIndexString !== 'string') return;

    const itemIndex = Number.parseInt(itemIndexString, 10);
    if (Number.isNaN(itemIndex)) return;

    const isInVisibleWindow =
      itemIndex >= firstVisibleItemIndex && itemIndex <= visibleWindowEndIndex;

    if (isInVisibleWindow) {
      item.removeAttribute('inert');
      item.removeAttribute('aria-hidden');
      return;
    }

    item.setAttribute('inert', 'true');
    item.setAttribute('aria-hidden', 'true');
  });
};

const resolvePageIndexFromVisibleItem = (
  firstVisibleItemIndex: number,
  pageItemGroups: number[][],
  totalPages: number,
) => {
  const pageIndex = pageItemGroups
    .map((page) => page[0])
    .reduce((resolvedPageIndex, pageStartIndex, pageIndex) => {
      if (pageStartIndex <= firstVisibleItemIndex) {
        return pageIndex;
      }

      return resolvedPageIndex;
    }, 0);

  return Math.min(pageIndex, Math.max(0, totalPages - 1));
};

type ResponsiveKeys =
  | 'itemsPerPage'
  | 'spaceBetweenItems'
  | 'scrollPadding'
  | 'showArrowsOnHover'
  | 'arrowSize'
  | 'hideDots';

export type CarouselProps = WithResponsive<CarouselBaseProps, ResponsiveKeys>;

const LOOP_VALUE_MAP = {
  infinite: 'infinite' as const,
  backToStart: 'native' as const,
  off: undefined,
};

const END_SCROLL_EPSILON = 1;

const buildCarouselWrapperClasses = (
  safeItemsPerPage: number,
  resolvedScrollPadding: string | undefined,
  carouselWrapperClassName: string,
): string => {
  const classes = [styles.carouselWrapper];
  if (!Number.isInteger(safeItemsPerPage)) {
    classes.push(styles.showPartialItems);
  }
  if (resolvedScrollPadding) {
    classes.push(styles.hasScrollPadding);
  }
  if (carouselWrapperClassName && carouselWrapperClassName.trim()) {
    classes.push(carouselWrapperClassName);
  } else {
    classes.push(styles.defaultCarouselWrapper);
  }
  return classes.join(' ');
};

const buildCssVariables = (
  resolvedItemsPerPage: number,
  safeItemsPerPage: number,
  resolvedSpaceBetweenItems: number,
  resolvedScrollPadding: string | undefined,
): Record<string, string> | undefined => {
  const cssVars: Record<string, string> = {};
  if (resolvedItemsPerPage !== 1) {
    cssVars['--items-per-page'] = String(safeItemsPerPage);
  }
  if (resolvedSpaceBetweenItems > 0) {
    cssVars['--space-between-items'] = `${resolvedSpaceBetweenItems}px`;
  }
  if (resolvedScrollPadding) {
    cssVars['--scroll-padding'] = resolvedScrollPadding;
  }
  return Object.keys(cssVars).length > 0 ? cssVars : undefined;
};

const buildScrollerStyles = (
  resolvedSpaceBetweenItems: number,
  resolvedItemsPerPage: number,
  safeItemsPerPage: number,
): React.CSSProperties => {
  const result: React.CSSProperties = {};
  if (resolvedSpaceBetweenItems >= 0) {
    result.gap = `${resolvedSpaceBetweenItems}px`;
  }
  if (resolvedItemsPerPage > 1 && resolvedSpaceBetweenItems > 0) {
    const gapSpacePerItem =
      (resolvedSpaceBetweenItems * (safeItemsPerPage - 1)) / safeItemsPerPage;
    result.gridAutoColumns = `calc(${100 / safeItemsPerPage}% - ${gapSpacePerItem}px)`;
  } else if (resolvedItemsPerPage > 1) {
    result.gridAutoColumns = `${100 / safeItemsPerPage}%`;
  }
  if (!Number.isInteger(safeItemsPerPage)) {
    result.scrollPaddingInlineStart = '1px';
  }
  return result;
};

const onCarouselKeyDown = (
  e: React.KeyboardEvent,
  carouselRef: React.RefObject<HTMLDivElement | null>,
  navigateToSlide: (index: number) => void,
  totalPages: number,
) => {
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    const prevButton = carouselRef.current?.querySelector(
      '[dir="prev"]',
    ) as HTMLElement | null;
    if (prevButton && prevButton.getAttribute('aria-disabled') !== 'true') {
      prevButton.click();
    }
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    const nextButton = carouselRef.current?.querySelector(
      '[dir="next"]',
    ) as HTMLElement | null;
    if (nextButton && nextButton.getAttribute('aria-disabled') !== 'true') {
      nextButton.click();
    }
  } else if (e.key === 'Home') {
    e.preventDefault();
    navigateToSlide(0);
  } else if (e.key === 'End') {
    e.preventDefault();
    navigateToSlide(totalPages - 1);
  }
};

const computePrevTargetPage = (
  canScrollPrev: boolean,
  currentPage: number,
  firstVisibleItemIndex: number,
  wholeItemsPerPage: number,
): number => {
  if (!canScrollPrev) return currentPage;
  if (firstVisibleItemIndex <= 0) return 0;
  return Math.max(0, Math.ceil(firstVisibleItemIndex / wholeItemsPerPage) - 1);
};

const computeNextTargetPage = (
  canScrollNext: boolean,
  currentPage: number,
  totalPages: number,
  firstVisibleItemIndex: number,
  wholeItemsPerPage: number,
): number => {
  if (!canScrollNext) return currentPage;
  return Math.min(
    totalPages - 1,
    Math.floor(firstVisibleItemIndex / wholeItemsPerPage) + 1,
  );
};

export const Carousel = ({
  children = [],
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedby,
  looping = 'off',
  itemsPerPage = 1,
  spaceBetweenItems = 0,
  scrollPadding,

  // Button and Controls Props
  iconType = 'chevron',
  arrowStyleVariant = 'neutral',
  arrowSize = 'md',
  hideDisabledArrow = false,
  showArrowsOnHover = false,
  focusStyle = 'default',

  // Dots/Tabs Props
  dotsSize = 'md',
  dotsVariant = 'standard',
  hideDots = false,

  // Autoplay Props
  autoPlay = false,
  autoPlayInterval = 2000,
  autoPlayStyleVariant = 'neutralVibrant',
  autoPlayControlSize = 'md',

  // Mouse Dragging
  mouseDragging = true,

  // Custom Class Names
  carouselWrapperClassName = '',
  prevArrowClassName = '',
  nextArrowClassName = '',
  autoplayControlClassName = '',
  dotsContainerClassName = '',
  dotsWrapperClassName = '',
  dotClassName = '',
  activeDotClassName = '',
  itemWrapperClassName = '',
  scrollerClassName = '',
  activeItemClassName = '',

  // Event Callbacks
  onArrowPress,
  onDotPress,
  onAutoplayPress,
  controlsRef,
  onNavigationStateChange,
  'data-testid': dataTestId,
}: CarouselProps) => {
  const items = React.Children.toArray(children);
  const totalItems = items.length;

  const breakpoint = useBreakpoint();
  const resolvedItemsPerPage =
    resolveResponsiveProp(itemsPerPage, breakpoint) ?? 1;
  const resolvedSpaceBetweenItems =
    resolveResponsiveProp(spaceBetweenItems, breakpoint) ?? 0;
  const resolvedScrollPadding =
    resolveResponsiveProp(scrollPadding, breakpoint) ?? undefined;
  const resolvedArrowSize =
    resolveResponsiveProp(arrowSize, breakpoint) ?? 'md';
  const resolvedHideDots = resolveResponsiveProp(hideDots, breakpoint) ?? false;

  // Ensure itemsPerPage is at least 1
  // We allow decimal values for showing partial items
  const safeItemsPerPage = Math.max(1, resolvedItemsPerPage);
  const wholeItemsPerPage = Math.max(1, Math.floor(safeItemsPerPage));

  const pageItemGroups = React.useMemo(() => {
    const groups = Array.from(
      { length: totalItems },
      (_, index) => index,
    ).reduce<number[][]>((accumulator, index) => {
      const currentGroup = accumulator.at(-1);

      if (currentGroup && currentGroup.length < wholeItemsPerPage) {
        currentGroup.push(index);
      } else {
        accumulator.push([index]);
      }

      return accumulator;
    }, []);

    if (groups.length >= 2) {
      const deficit = wholeItemsPerPage - groups.at(-1)!.length;

      if (deficit > 0) {
        const fill = [...groups.at(-2)!].splice(wholeItemsPerPage - deficit);
        groups.at(-1)!.unshift(...fill);
      }
    }

    return groups;
  }, [totalItems, wholeItemsPerPage]);

  const carouselRef = useRef<HTMLDivElement>(null);
  const ariaCarouselRef = useRef<HTMLDivElement>(null);
  const dragEndResetTimeoutRef = useRef<number | null>(null);
  const dragEndSyncTimeoutRef = useRef<number | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const itemElementsRef = useRef<HTMLElement[]>([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [prevPage, setPrevPage] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(pageItemGroups.length > 1);
  const [ariaTabsPageCount, setAriaTabsPageCount] = useState(0);
  const [firstVisibleItemIndex, setFirstVisibleItemIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isTransitioning] = useState(false);

  // Add state variables for drag/link handling
  const [dragStartPosition, setDragStartPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const dragThreshold = 5; // Pixels of movement needed to consider as a drag
  const loopMode = LOOP_VALUE_MAP[looping];
  const totalPages =
    ariaTabsPageCount > 0 ? ariaTabsPageCount : pageItemGroups.length;

  useEffect(() => {
    const lastPage = Math.max(0, totalPages - 1);

    setCurrentPage((previousPage) => Math.min(previousPage, lastPage));
  }, [totalPages]);

  // Determine if dots should be shown
  // Hide dots if:
  // 1. resolvedHideDots prop is true (responsive), OR
  // 2. There's only one page (totalPages <= 1)
  const shouldShowDots = !resolvedHideDots && totalPages > 1;

  const getScrollerElement = useCallback(() => {
    return carouselRef.current?.querySelector(
      '[data-carousel-scroller="true"]',
    ) as HTMLElement | null;
  }, []);

  const scrollToPage = useCallback(
    (scroller: HTMLElement, pageIndex: number) => {
      const itemsPerPageWhole = Math.max(1, Math.floor(safeItemsPerPage));
      const targetItemIndex = pageIndex * itemsPerPageWhole;
      const targetItem = scroller.querySelector(
        `[data-item-index="${targetItemIndex}"]`,
      ) as HTMLElement | null;

      if (!targetItem) {
        return false;
      }

      const scrollerRect = scroller.getBoundingClientRect();
      const itemRect = targetItem.getBoundingClientRect();
      const scrollerPaddingLeft =
        parseFloat(getComputedStyle(scroller).paddingLeft) || 0;
      const maxScrollLeft = Math.max(
        0,
        scroller.scrollWidth - scroller.clientWidth,
      );
      const targetScrollLeft =
        scroller.scrollLeft +
        (itemRect.left - scrollerRect.left) -
        scrollerPaddingLeft;
      const clampedTargetScrollLeft = Math.min(
        Math.max(0, targetScrollLeft),
        maxScrollLeft,
      );

      scroller.scrollTo({
        left: clampedTargetScrollLeft,
        behavior: 'smooth',
      });

      return true;
    },
    [safeItemsPerPage],
  );

  const navigateToSlide = useCallback(
    (index: number) => {
      if (index === currentPage || index < 0 || index >= totalPages) {
        return;
      }

      // Find and click the corresponding hidden tab
      if (carouselRef.current) {
        const targetTab = carouselRef.current.querySelector(
          `[data-slide-tab="true"][data-index="${index}"]`,
        ) as HTMLElement;

        if (targetTab) {
          targetTab.click();
          setPrevPage(currentPage);
          setCurrentPage(index);
        }
      }
    },
    [currentPage, totalPages],
  );

  const syncCurrentPageFromVisibleItem = useCallback(() => {
    if (!carouselRef.current || totalPages <= 1) {
      return;
    }

    const scroller = carouselRef.current.querySelector(
      '[data-carousel-scroller="true"]',
    ) as HTMLElement | null;

    if (!scroller) {
      return;
    }

    const itemElements = Array.from(
      scroller.querySelectorAll('[data-item-index]'),
    ) as HTMLElement[];

    if (!itemElements.length) {
      return;
    }

    const scrollerLeft = scroller.getBoundingClientRect().left;
    let closestItemIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    itemElements.forEach((element) => {
      const index = Number.parseInt(element.dataset.itemIndex ?? '', 10);

      if (Number.isNaN(index)) {
        return;
      }

      const distance = Math.abs(
        element.getBoundingClientRect().left - scrollerLeft,
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestItemIndex = index;
      }
    });

    const maxScrollLeft = Math.max(
      0,
      scroller.scrollWidth - scroller.clientWidth,
    );
    const isAtEnd =
      maxScrollLeft > 0 &&
      scroller.scrollLeft >= maxScrollLeft - END_SCROLL_EPSILON;
    const itemsPerPageWhole = Math.max(1, Math.floor(safeItemsPerPage));
    const nextPage = isAtEnd
      ? totalPages - 1
      : Math.min(
          totalPages - 1,
          Math.floor(closestItemIndex / itemsPerPageWhole),
        );

    if (nextPage !== currentPage) {
      setPrevPage(currentPage);
      setCurrentPage(nextPage);
    }
  }, [currentPage, safeItemsPerPage, totalPages]);

  const navigateToPreviousSlide = useCallback(() => {
    const scroller = getScrollerElement();

    if (scroller) {
      const targetPage = Math.max(0, currentPage - 1);
      const didScrollToItemGroup = scrollToPage(scroller, targetPage);

      if (!didScrollToItemGroup) {
        scroller.scrollBy({
          left: -scroller.clientWidth,
          behavior: 'smooth',
        });
      }

      setPrevPage(currentPage);
      setCurrentPage(targetPage);

      window.setTimeout(() => {
        syncCurrentPageFromVisibleItem();
      }, 200);
      return;
    }

    if (currentPage <= 0) {
      return;
    }

    navigateToSlide(currentPage - 1);
  }, [
    currentPage,
    getScrollerElement,
    navigateToSlide,
    scrollToPage,
    syncCurrentPageFromVisibleItem,
  ]);

  const navigateToNextSlide = useCallback(() => {
    const scroller = getScrollerElement();

    if (scroller) {
      const targetPage = Math.min(totalPages - 1, currentPage + 1);
      const didScrollToItemGroup = scrollToPage(scroller, targetPage);

      if (!didScrollToItemGroup) {
        scroller.scrollBy({
          left: scroller.clientWidth,
          behavior: 'smooth',
        });
      }

      setPrevPage(currentPage);
      setCurrentPage(targetPage);

      window.setTimeout(() => {
        syncCurrentPageFromVisibleItem();
      }, 200);
      return;
    }

    if (currentPage >= totalPages - 1) {
      return;
    }

    navigateToSlide(currentPage + 1);
  }, [
    currentPage,
    getScrollerElement,
    navigateToSlide,
    scrollToPage,
    syncCurrentPageFromVisibleItem,
    totalPages,
  ]);

  useEffect(() => {
    const nextCanGoPrev = canScrollPrev || currentPage > 0;
    const nextCanGoNext = canScrollNext || currentPage < totalPages - 1;

    const navigationState = toNavigationState(
      nextCanGoPrev,
      nextCanGoNext,
      currentPage,
      totalPages,
    );

    if (!controlsRef) {
      onNavigationStateChange?.(navigationState);
      return;
    }

    controlsRef.current = {
      prev: navigateToPreviousSlide,
      next: navigateToNextSlide,
      goToSlide: navigateToSlide,
      ...navigationState,
    };

    onNavigationStateChange?.(navigationState);

    return () => {
      if (controlsRef.current) {
        controlsRef.current = null;
      }
    };
  }, [
    controlsRef,
    canScrollNext,
    canScrollPrev,
    currentPage,
    navigateToNextSlide,
    navigateToPreviousSlide,
    navigateToSlide,
    onNavigationStateChange,
    totalPages,
  ]);

  useEffect(() => {
    return () => {
      if (dragEndResetTimeoutRef.current !== null) {
        window.clearTimeout(dragEndResetTimeoutRef.current);
      }

      if (dragEndSyncTimeoutRef.current !== null) {
        window.clearTimeout(dragEndSyncTimeoutRef.current);
      }
    };
  }, []);

  // Add effect to handle link click prevention during drag
  useEffect(() => {
    if (!carouselRef.current || !mouseDragging) return;

    // Function to prevent link activation during/after drag
    const preventLinkActivation = (e: MouseEvent) => {
      if (!isDragging) return;

      const target = e.target;
      if (!(target instanceof Element)) return;

      const link = target.closest('a[href]');
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();

      // Reset isDragging after the current event cycle completes
      setTimeout(() => {
        setIsDragging(false);
      }, 0);
    };

    // Function to handle mouse down events
    const handleMouseDown = (e: MouseEvent) => {
      setDragStartPosition({ x: e.clientX, y: e.clientY });
    };

    // Function to handle mouse move events
    const handleMouseMove = (e: MouseEvent) => {
      if (dragStartPosition === null) return;

      // Calculate the distance moved
      const dx = Math.abs(e.clientX - dragStartPosition.x);
      const dy = Math.abs(e.clientY - dragStartPosition.y);
      const distance = Math.sqrt(dx * dx + dy * dy);

      // If the user has moved more than the threshold, consider it a drag
      if (distance > dragThreshold) {
        setIsDragging(true);
      }
    };

    // Function to handle mouse up events
    const handleMouseUp = () => {
      // If this wasn't a drag, don't interfere with normal clicks
      if (!isDragging) {
        setDragStartPosition(null);
        return;
      }

      // Add a brief delay for the click prevention to work
      setTimeout(() => {
        setIsDragging(false);
        setDragStartPosition(null);
      }, 50);
    };

    // Add event listeners for the carousel container
    const carouselElement = carouselRef.current;

    // Capture phase is important to catch events before they reach the links
    carouselElement.addEventListener('mousedown', handleMouseDown, true);
    carouselElement.addEventListener('mousemove', handleMouseMove, true);
    carouselElement.addEventListener('mouseup', handleMouseUp, true);

    // This is the key event listener that prevents link activation
    carouselElement.addEventListener('click', preventLinkActivation, true);

    return () => {
      // Clean up event listeners
      carouselElement.removeEventListener('mousedown', handleMouseDown, true);
      carouselElement.removeEventListener('mousemove', handleMouseMove, true);
      carouselElement.removeEventListener('mouseup', handleMouseUp, true);
      carouselElement.removeEventListener('click', preventLinkActivation, true);
    };
  }, [mouseDragging, isDragging, dragStartPosition, dragThreshold]);

  // Handle transition direction
  useEffect(() => {
    if (carouselRef.current) {
      const scroller = carouselRef.current.querySelector(`.${styles.scroller}`);
      if (scroller && scroller instanceof HTMLElement) {
        if (prevPage < currentPage) {
          scroller.classList.remove(styles.slideLeft);
          scroller.classList.add(styles.slideRight);
        } else if (prevPage > currentPage) {
          scroller.classList.remove(styles.slideRight);
          scroller.classList.add(styles.slideLeft);
        }
      }
    }
  }, [currentPage, prevPage]);

  // Keep total page count aligned with the pages emitted by react-aria-carousel.
  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (!carouselElement) return;

    let animationFrameId: number | null = null;

    const updateAriaPageCount = () => {
      const nextCount = carouselElement.querySelectorAll(
        '[data-slide-tab="true"]',
      ).length;

      setAriaTabsPageCount((previousCount) =>
        previousCount === nextCount ? previousCount : nextCount,
      );
    };

    const scheduleAriaPageCountUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        updateAriaPageCount();
      });
    };

    const observer = new MutationObserver(() => {
      scheduleAriaPageCountUpdate();
    });

    observer.observe(carouselElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-slide-tab'],
    });

    scheduleAriaPageCountUpdate();

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      observer.disconnect();
    };
  }, [totalItems, safeItemsPerPage]);

  // Track the actual scroll position so arrow state matches mouse dragging,
  // even when the underlying library still considers the first page active.
  useEffect(() => {
    const scroller =
      (getScrollerElement() as HTMLDivElement | null) ?? scrollerRef.current;
    if (!scroller) return;

    let animationFrameId: number | null = null;

    const scheduleScrollNavigationUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(() => {
        animationFrameId = null;
        updateScrollNavigation();
      });
    };

    const updateScrollNavigation = () => {
      const {
        endEpsilon,
        epsilon,
        hasScrollableOverflow,
        maxScrollLeft,
        nextCanScrollPrev,
        nextCanScrollNext,
        scrollLeft,
      } = getScrollMetrics(scroller, currentPage, totalPages);

      const itemElements = getCachedItemElements(scroller, itemElementsRef);

      const safeFirstVisibleItemIndex = resolveFirstVisibleItemIndex(
        scroller,
        itemElements,
        scrollLeft,
        epsilon,
      );

      syncItemInteractivity(
        itemElements,
        safeFirstVisibleItemIndex,
        safeItemsPerPage,
      );

      const nextPageIndexFromVisibleItem = resolvePageIndexFromVisibleItem(
        safeFirstVisibleItemIndex,
        pageItemGroups,
        totalPages,
      );
      const isAtEnd =
        hasScrollableOverflow && scrollLeft >= maxScrollLeft - endEpsilon;

      let nextPageIndex = currentPage;
      if (hasScrollableOverflow) {
        nextPageIndex = isAtEnd ? totalPages - 1 : nextPageIndexFromVisibleItem;
      }

      setCanScrollPrev((previousValue) =>
        previousValue === nextCanScrollPrev ? previousValue : nextCanScrollPrev,
      );
      setCanScrollNext((previousValue) =>
        previousValue === nextCanScrollNext ? previousValue : nextCanScrollNext,
      );
      setFirstVisibleItemIndex((previousValue) => {
        const nextIndex = safeFirstVisibleItemIndex;
        return previousValue === nextIndex ? previousValue : nextIndex;
      });
      setCurrentPage((previousPage) => {
        if (previousPage !== nextPageIndex) {
          setPrevPage(previousPage);
        }

        return previousPage === nextPageIndex ? previousPage : nextPageIndex;
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      scheduleScrollNavigationUpdate();
    });

    const refreshItemElements = () => {
      const nextItemElements = Array.from(
        scroller.querySelectorAll<HTMLElement>('[data-item-index]'),
      );
      itemElementsRef.current = nextItemElements;
      return nextItemElements;
    };

    resizeObserver.observe(scroller);

    const itemElements = refreshItemElements();
    itemElements.forEach((item) => {
      resizeObserver.observe(item);
    });

    const handleImageLoad = () => {
      scheduleScrollNavigationUpdate();
    };

    scroller.addEventListener('load', handleImageLoad, true);
    updateScrollNavigation();
    scroller.addEventListener('scroll', updateScrollNavigation, {
      passive: true,
    });
    window.addEventListener('resize', scheduleScrollNavigationUpdate);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      resizeObserver.disconnect();
      scroller.removeEventListener('load', handleImageLoad, true);
      scroller.removeEventListener('scroll', updateScrollNavigation);
      window.removeEventListener('resize', scheduleScrollNavigationUpdate);
      itemElementsRef.current = [];
    };
  }, [
    currentPage,
    getScrollerElement,
    pageItemGroups,
    safeItemsPerPage,
    totalPages,
    totalItems,
  ]);

  const handleActivePageIndexChange = ({ index }: { index: number }) => {
    const scroller = getScrollerElement();
    const maxScrollLeft = scroller
      ? Math.max(0, scroller.scrollWidth - scroller.clientWidth)
      : 0;
    const isAtEnd =
      scroller !== null &&
      maxScrollLeft > 0 &&
      scroller.scrollLeft >= maxScrollLeft - END_SCROLL_EPSILON;
    const nextPage = isAtEnd ? totalPages - 1 : index;

    setCurrentPage((previousPage) => {
      if (previousPage !== nextPage) {
        setPrevPage(previousPage);
      }

      return nextPage;
    });
  };

  // Interaction handlers for hover and focus
  const interactionHandlers = showArrowsOnHover
    ? {
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
        onFocus: () => setIsFocused(true),
        onBlur: (e: React.FocusEvent) => {
          // Only blur if focus is moving outside the carousel
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsFocused(false);
          }
        },
      }
    : {};

  // Determine whether arrows should be visible
  // Hide arrows if:
  // 1. There's only one page (all items fit on screen), OR
  // 2. showArrowsOnHover is true and carousel is not hovered/focused
  const shouldShowArrows = totalPages > 1;
  const arrowsVisible =
    shouldShowArrows && (!showArrowsOnHover || isHovered || isFocused);

  const cssVars = buildCssVariables(
    resolvedItemsPerPage,
    safeItemsPerPage,
    resolvedSpaceBetweenItems,
    resolvedScrollPadding,
  );

  const { focusProps, isFocusVisible } = useFocusRing();

  const activeItemIndices = pageItemGroups[currentPage] ?? [];

  const autoplayStatus = autoPlay ? ', autoplay enabled' : '';
  const effectiveAriaLabel =
    ariaLabel ??
    `Carousel with ${totalItems} items, currently showing slide ${currentPage + 1} of ${totalPages}${autoplayStatus}`;

  const onScrollerKeyDown = useCallback(
    (e: React.KeyboardEvent) =>
      onCarouselKeyDown(e, carouselRef, navigateToSlide, totalPages),
    [navigateToSlide, totalPages],
  );

  return (
    <div
      className={buildCarouselWrapperClasses(
        safeItemsPerPage,
        resolvedScrollPadding,
        carouselWrapperClassName,
      )}
      ref={carouselRef}
      style={cssVars as React.CSSProperties}
      data-testid={dataTestId}
      data-dots-size={dotsSize}
      {...interactionHandlers}
      data-arrows-visible={arrowsVisible ? 'true' : 'false'}
      data-transitioning={isTransitioning ? 'true' : undefined}
      data-scroll-padding={resolvedScrollPadding}
      role="region"
      aria-label={effectiveAriaLabel}
      aria-describedby={ariaDescribedby}
      aria-roledescription="carousel"
    >
      <AriaCarousel
        className={`${carousel({ looping })}`}
        loop={loopMode}
        ref={ariaCarouselRef}
        autoplay={autoPlay}
        autoplayInterval={autoPlayInterval}
        mouseDragging={mouseDragging && !isTransitioning}
        onActivePageIndexChange={handleActivePageIndexChange}
        onDragStart={(e) => {
          setIsDragging(true);
          if (e && e.nativeEvent) {
            setDragStartPosition({
              x: e.nativeEvent.clientX,
              y: e.nativeEvent.clientY,
            });
          }
        }}
        onDragEnd={() => {
          // We keep isDragging true for a short time after drag ends
          // This helps ensure click events don't fire immediately after dragging
          if (dragEndResetTimeoutRef.current !== null) {
            window.clearTimeout(dragEndResetTimeoutRef.current);
          }

          if (dragEndSyncTimeoutRef.current !== null) {
            window.clearTimeout(dragEndSyncTimeoutRef.current);
          }

          dragEndResetTimeoutRef.current = window.setTimeout(() => {
            setIsDragging(false);
            syncCurrentPageFromVisibleItem();
          }, 50);

          dragEndSyncTimeoutRef.current = window.setTimeout(() => {
            syncCurrentPageFromVisibleItem();
          }, 150);
        }}
        data-dragging={isDragging ? 'true' : 'false'}
        itemsPerPage={safeItemsPerPage}
        scrollPadding={resolvedScrollPadding}
        data-has-space-between={
          resolvedSpaceBetweenItems > 0 ? 'true' : undefined
        }
        data-items-per-page={
          resolvedItemsPerPage !== 1 ? safeItemsPerPage.toString() : undefined
        }
      >
        <AriaCarouselScroller
          className={`${styles.scroller} ${focusStyleVariants({ focusStyle })} ${scrollerClassName}`.trim()}
          ref={scrollerRef}
          data-carousel-scroller="true"
          {...focusProps}
          data-focused={isFocusVisible ? true : undefined}
          data-focus-visible={isFocusVisible ? true : undefined}
          data-transitioning={isTransitioning ? 'true' : undefined}
          style={buildScrollerStyles(
            resolvedSpaceBetweenItems,
            resolvedItemsPerPage,
            safeItemsPerPage,
          )}
          tabIndex={0}
          role="region"
          aria-label="Carousel content"
          onKeyDown={onScrollerKeyDown}
        >
          {items.map((child, index) => {
            const isActiveItem = activeItemIndices.includes(index);
            const itemClasses = [
              itemWrapperClassName || '',
              styles.item,
              isActiveItem && activeItemClassName ? activeItemClassName : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <AriaCarouselItem
                key={index}
                className={itemClasses}
                data-is-active={isActiveItem ? 'true' : undefined}
                data-item-index={index}
                aria-label={`Slide ${index + 1} of ${totalItems}`}
              >
                {child}
              </AriaCarouselItem>
            );
          })}
        </AriaCarouselScroller>

        <div
          className={`${styles.controls} ${showArrowsOnHover ? styles.showOnHover : ''}`}
        >
          <CarouselButton
            dir="prev"
            variant={arrowStyleVariant}
            size={resolvedArrowSize}
            focusStyle={focusStyle}
            iconType={iconType}
            hideDisabledArrow={hideDisabledArrow}
            overrideHideDisabledArrow={loopMode ? undefined : !canScrollPrev}
            onClick={
              loopMode
                ? undefined
                : () =>
                    navigateToSlide(
                      computePrevTargetPage(
                        canScrollPrev,
                        currentPage,
                        firstVisibleItemIndex,
                        wholeItemsPerPage,
                      ),
                    )
            }
            onPress={() => onArrowPress?.('prev')}
            className={`${prevArrowClassName || styles.defaultPrevButton} ${!arrowsVisible ? styles.hidden : ''}`}
          />

          <CarouselButton
            dir="next"
            variant={arrowStyleVariant}
            size={resolvedArrowSize}
            focusStyle={focusStyle}
            iconType={iconType}
            hideDisabledArrow={hideDisabledArrow}
            overrideHideDisabledArrow={loopMode ? undefined : !canScrollNext}
            onClick={
              loopMode
                ? undefined
                : () =>
                    navigateToSlide(
                      computeNextTargetPage(
                        canScrollNext,
                        currentPage,
                        totalPages,
                        firstVisibleItemIndex,
                        wholeItemsPerPage,
                      ),
                    )
            }
            onPress={() => onArrowPress?.('next')}
            className={`${nextArrowClassName || styles.defaultNextButton} ${!arrowsVisible ? styles.hidden : ''}`}
          />
        </div>

        <div className={styles.hiddenTabs}>
          <AriaCarouselTabs>
            {(page) => (
              <AriaCarouselTab
                key={page.index}
                index={page.index}
                data-index={page.index}
                data-slide-tab="true"
                tabIndex={-1}
              />
            )}
          </AriaCarouselTabs>
        </div>

        {autoPlay && (
          <div
            className={`${styles.autoplayControlWrapper} ${autoplayControlClassName || styles.defaultAutoplayControl}`}
          >
            <AutoplayControl
              variant={autoPlayStyleVariant}
              size={autoPlayControlSize}
              className=""
              onPress={onAutoplayPress}
            />
          </div>
        )}

        {shouldShowDots && (
          <CarouselDots
            totalItems={totalPages}
            currentPage={currentPage}
            onDotClick={(index) => navigateToSlide(index)}
            onPress={onDotPress}
            focusStyle={focusStyle}
            dotsSize={dotsSize}
            variant={dotsVariant}
            className={dotsContainerClassName || styles.defaultDotsContainer}
            dotsWrapperClassName={dotsWrapperClassName}
            dotClassName={dotClassName}
            activeDotClassName={activeDotClassName}
            isTransitioning={isTransitioning}
          />
        )}
      </AriaCarousel>
    </div>
  );
};

export default Carousel;
